import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  Snackbar,
  Button,
  LoadingIndicator,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import React, { useState } from "react";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { DownloadCustomerApp } from "../atoms/DownloadCustomerApp";
import { useAppSelector } from "../../../store";
import { CustomerAppTaskModal } from "../molecules/CustomerAppTaskModal";
import { SplatProspect } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { Box } from "components/atoms/Box";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { sendCustomerAppDownloadEmail } from "services/email";
import { Mixpanel } from "providers/mixpanel";
import { CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_EMAIL_TO_CUSTOMER_IN_CUSTOMER_APP_TASK_VIEW } from "constants/mixpanelEvents";
import { rollbar } from "providers/rollbar";
import { useCustomerAppSessionFlag } from "checkout/hooks/useCustomerAppSessionFlag";

const CustomerAppAffiliateTaskView: React.FC = () => {
  const { prospectId, navigateToNextTask, offer } = useCheckoutContext();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);

  const [loadingOverlay, setLoadingOverlay] = useState({
    message: "",
    show: false,
  });
  const [snackbarBottom, setSnackbarBottom] = useState({
    message: "",
    show: false,
    type: "success",
  });
  const [showModal, setShowModal] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useCustomerAppSessionFlag(
    prospectId,
    offer?.id ?? ""
  );

  const { data: prospect, isLoading: isProspectLoading } =
    useProspect(prospectId);

  const handleSubmit = async () => {
    setShowModal(false);
    setLoadingOverlay({ message: "Sending email...", show: true });

    try {
      await sendCustomerAppDownloadEmail(authKey, prospectId);
      setSnackbarBottom({
        message: "Email sent successfully!",
        show: true,
        type: "success",
      });
    } catch (error) {
      rollbar.error(error as Error);
      setSnackbarBottom({
        message: "Sorry, we couldn't send your email.",
        show: true,
        type: "error",
      });
    } finally {
      setLoadingOverlay({ message: "", show: false });
    }
  };

  useCheckoutFooter(
    isProspectLoading ? null : (
      <CheckoutFooter
        buttonText="Confirm"
        onClick={() => navigateToNextTask()}
        checkboxLabel="I sent the invitation to customer"
        checkboxChecked={checkboxChecked}
        onCheckboxChange={setCheckboxChecked}
      />
    ),
    [isProspectLoading, navigateToNextTask, checkboxChecked, setCheckboxChecked]
  );

  if (isProspectLoading) {
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" />
      </LoadingWrapper>
    );
  }

  const prospectData = prospect as SplatProspect;

  return (
    <Box display="flex" flexDirection="column">
      <InformationContainer data-testid="validation-info">
        <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
          Please make sure the customer has downloaded the mobile app. Scan the
          QR code, send it by email or text message with the the app download
          link
        </Typography>
        <StyledButton
          color="secondary"
          size="sm"
          onClick={() => {
            setShowModal(true);
          }}
          disabled={loadingOverlay.show || !prospectId}
        >
          Send email to customer
        </StyledButton>
      </InformationContainer>
      <Container>
        <DownloadCustomerApp isPartner={true} />
      </Container>
      <Snackbar
        data-testid="bottom-snackbar"
        open={snackbarBottom.show}
        onClose={() =>
          setSnackbarBottom({ message: "", show: false, type: "success" })
        }
        message={snackbarBottom.message}
        type={snackbarBottom.type as "success" | "error"}
      />
      {loadingOverlay.show && (
        <LoadingOverlay
          message={loadingOverlay.message}
          data-testid="customer-app-task-loading-overlay"
        />
      )}
      {showModal && (
        <CustomerAppTaskModal
          name={prospect?.customerFirstName + " " + prospect?.customerLastName}
          email={prospect?.customerEmail ?? ""}
          phone={prospect?.customerPrimaryPhone ?? ""}
          onClose={() => setShowModal(false)}
          onSendEmail={() => {
            Mixpanel.track(
              CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_EMAIL_TO_CUSTOMER_IN_CUSTOMER_APP_TASK_VIEW,
              {
                prospectId,
                salesRep: prospectData?.salesRep,
                salesRepEmail: prospectData?.salesRepEmail,
                salesRepName: prospectData?.salesRepName,
                opportunityStage: prospectData?.leadStage,
              }
            );

            handleSubmit();
          }}
        />
      )}
    </Box>
  );
};

const LoadingWrapper = styled.div`
  margin-top: 32px;
`;

const InformationContainer = styled.div`
  margin-bottom: 16px;
  > p {
    padding-bottom: 16px;
  }
`;

const Container = styled.div`
  margin-top: 26px;
`;

const StyledButton = styled(Button)`
  margin-top: 4px;
`;

export { CustomerAppAffiliateTaskView };
